import { formatCurrencyBRL } from '@utils/currency.util';
import { v4 as uuid } from 'uuid';

import {
  Offer,
  OfferFormData,
  PaymentMethods,
} from '../interfaces/offersInterface';

export const formOfferFactory = (data: Offer): OfferFormData => {
  const paymentMethods: PaymentMethods[] = [];
  if (data.isAvailableCreditCard) paymentMethods.push('creditCard');
  if (data.isAvailableNuPay) paymentMethods.push('nupay');
  if (data.isAvailablePagaleve) paymentMethods.push('pagaleve');
  if (data.isAvailablePix) paymentMethods.push('pix');
  if (data.isAvailableBillet) paymentMethods.push('billet');

  return {
    offerName: `${data.name}`,
    offerDescription: data.description,
    bannerWeb: data.checkout?.bannerWeb && `${data.checkout?.bannerWeb}`,
    bannerMobile:
      data.checkout?.bannerMobile && `${data.checkout?.bannerMobile}`,
    bannerSideWeb:
      data.checkout?.bannerSideWeb && `${data.checkout?.bannerSideWeb}`,
    logo: data.checkout?.logo && `${data.checkout?.logo}`,
    accessProfileId: data.accessProfileId ?? '',
    sensitiveData: {
      acquisition: String(data.acquisitionId),
      seasonality: String(data.seasonalityId),
      type: String(data.typeId),
      fullPrice: formatCurrencyBRL(data.fullPrice),
      hasRetryDiscounts:
        data.discounts?.filter(({ active }) => active).length > 0 || false,
      retryDiscounts:
        data.discounts?.map(({ active, percentage, isDirectFallback }) => ({
          key: uuid(),
          active,
          value: formatCurrencyBRL(
            +(data.fullPrice - (data.fullPrice * percentage) / 100).toFixed(2)
          ),
          typeDiscount: 'percentage' as const,
          percentage: `${percentage}`,
          isDirectFallback,
        })) || [],
      installments: data.installments.toString(),
      displayPrice: data.displayPrice
        ? formatCurrencyBRL(data.displayPrice)
        : '',
      startDate: data.startDate,
      endDate: data?.endDate ?? '',
      purchaseBtnLabel: data.checkout?.purchaseBtnLabel ?? '',
      offerStandard: data?.replaceDefaultOffer || false,
      plan: data?.accessProfileId ? String(data.accessProfileId) : undefined,
      isMailPromotional: data.tag === 'promotional',
      renewalDisabled: data?.renewalDisabled,
      onlyFull: data.onlyFull || false,
      renewalOfferId: String(data.renewalOfferId ?? ''),
      profileId: String(data.profileId ?? ''),
      warrantyDays: data.warrantyDays,
      paymentMethods,
      isDisplayCoupon: data?.isDisplayCoupon,
      flowId: data.flowId ? String(data.flowId) : '',
      disclaimer: data.disclaimer ?? '',
      termsRef: data.checkout?.termsRef ?? '',
      privacyPolicyRef: data.checkout?.privacyPolicyRef ?? '',
    },
    analytics: {
      googleAnalyticsIds: data.checkout?.googleAnalyticsIds ?? '',
      facebookPixels: data.checkout?.facebookPixels ?? '',
    },
    hasPromotionalOffer: !!data.offerFormat,
    promotionalOffer: {
      promotionalAmount: !!data.offerFormat
        ? formatCurrencyBRL(data.offerFormat?.promotionalAmount ?? 0)
        : '',
      qtdPromotionalDays: !!data.offerFormat
        ? String(data.offerFormat?.qtdPromotionalDays ?? '')
        : '',
    },
    hasCounter:
      data.checkout?.enableCounter ||
      !!data.uniqueVisitCounter ||
      !!data.enableMultiCounter,
    counter: {
      typeCounter: !!data.uniqueVisitCounter
        ? 'uniqueVisitCounter'
        : !!data.enableMultiCounter
          ? 'multiCounter'
          : 'offerCounter',
      offerCounter: {
        rightImg: `${data.checkout?.bannerRightWebCounter}`,
        rightImgMobile: `${data.checkout?.bannerRightMobileCounter}`,
        leftImg: `${data.checkout?.bannerLeftCounter}`,
        leftImgMobile: `${data.checkout?.bannerLeftMobileCounter}`,
        bgCounter: data.checkout?.colorBackgroundCounter ?? '',
      },
      uniqueVisitCounter: {
        timeoutRedirectUrl: data.uniqueVisitCounter?.timeoutRedirectUrl ?? '',
        timerMinute: String(data.uniqueVisitCounter?.timerMinute ?? ''),
        timerSecond: String(data.uniqueVisitCounter?.timerSecond ?? ''),
        countdownPhrase: data.uniqueVisitCounter?.countdownPhrase ?? '',
        cacheExpirationTime: String(
          data.uniqueVisitCounter?.cacheExpirationTime ?? ''
        ),
      },
      multiCounter: {
        bgCounter: data.multiCounter?.bgCounter ?? '',
        rightImg: data.multiCounter?.rightImg
          ? `${data.multiCounter?.rightImg}`
          : '',
        rightImgMobile: data.multiCounter?.rightImgMobile
          ? `${data.multiCounter?.rightImgMobile}`
          : '',
        leftImg: data.multiCounter?.leftImg
          ? `${data.multiCounter?.leftImg}`
          : '',
        leftImgMobile: data.multiCounter?.leftImgMobile
          ? `${data.multiCounter?.leftImgMobile}`
          : '',
        schedules:
          data.multiCounter?.schedules.map(schedule => ({
            startTime: schedule.startTime,
            endTime: schedule.endTime,
          })) || [],
      },
    },
    isPhysicProduct: data.productSellGroups?.prodType === 'fisico',
    physicProduct: {
      isFreeShipping: data.isFreeShipping,
      primaryColor: data.checkout?.primaryColor ?? '#FF4F00',
      footerText: data.checkout?.footerText ?? '',
      seo: {
        title: data.checkout?.title ?? '',
        smallDescription: data.checkout?.smallDescription ?? '',
        largeDescription: data.checkout?.largeDescription ?? '',
        image: data.checkout?.image ? `${data.checkout?.image}` : '',
        favicon: data.checkout?.favicon ? `${data.checkout.favicon}` : '',
        siteName: data.checkout?.siteName ?? '',
        keywords: data.checkout?.keywords ? data.checkout.keywords : [],
      },
    },
    isAvailableMobile: data.isAvailableMobile ?? false,
    mobile: {
      itunesStoreId: data.itunesStoreId ?? '',
      googleStoreId: data.googleStoreId ?? '',
    },
  };
};
